import { Link } from 'react-router-dom';

/** Placeholder Terms of Service. Signals to visitors that Sendepot HAS
 * terms (and to link crawlers that the page exists), but the real
 * language must be produced by counsel before opening self-serve signups.
 * Replace every {{PLACEHOLDER}} bracket below and get a lawyer review. */
export default function Terms() {
  return (
    <div className="min-h-screen bg-bg text-ink">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-xs text-dim mb-6">
          <Link to="/" className="hover:text-ink transition">← Home</Link>
        </div>
        <h1 className="text-2xl font-medium mb-2">Terms of Service</h1>
        <div className="text-xs text-dim mb-8">Effective: {'{{EFFECTIVE_DATE}}'}</div>

        <div className="text-sm text-warn/90 border border-warn/30 rounded p-3 mb-8">
          <strong>Placeholder.</strong> This page is a stub until Sendepot's
          Terms of Service are reviewed by counsel. Do not rely on it for
          any legal purpose. Use of the service today is on a best-effort
          basis under a direct agreement with Sendepot.
        </div>

        <Section title="1. The service">
          Sendepot is a platform for provisioning Microsoft 365 tenants and
          connecting the resulting mailboxes to third-party sequencer
          platforms (Instantly.ai, Smartlead, and similar). You use it to
          operate your own outbound sending — the mailboxes belong to
          you, the underlying Microsoft tenants are billed to you, and
          the sequencer accounts are yours.
        </Section>

        <Section title="2. Your account">
          You are responsible for keeping your workspace credentials
          secure, for the Microsoft admin credentials you upload, and for
          the actions taken by anyone using API tokens you issue. Notify
          us immediately at support@sendepot.com if you believe a
          credential has been exposed.
        </Section>

        <Section title="3. Acceptable use">
          You may not use Sendepot to send unlawful, deceptive, or
          harassing messages, to impersonate third parties, or to
          circumvent CAN-SPAM / GDPR / CASL / any applicable anti-spam
          regime. We reserve the right to suspend or terminate access
          for suspected abuse.
        </Section>

        <Section title="4. Data we process">
          The specifics live in our{' '}
          <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>
          . In summary: we store the Microsoft tenant identifiers and
          credentials required to provision on your behalf; we store
          mailbox metadata and send-related telemetry; we do NOT read
          the content of emails your mailboxes send or receive.
        </Section>

        <Section title="5. Fees & termination">
          Fees, trial windows, and refund policy are described on the
          pricing page and in your subscription plan. You may terminate
          at any time; on termination we retain provisioning records for
          {'{{RETENTION_WINDOW}}'} for audit/tax purposes, then
          delete.
        </Section>

        <Section title="6. Warranties & liability">
          Sendepot is provided "as is." We disclaim implied warranties
          to the maximum extent permitted by law. Liability is capped at
          the fees paid in the twelve months preceding the claim.
        </Section>

        <Section title="7. Contact">
          Questions: <a href="mailto:support@sendepot.com" className="text-accent hover:underline">support@sendepot.com</a>.
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="text-sm text-ink font-medium mb-2">{title}</h2>
      <p className="text-sm text-dim leading-relaxed">{children}</p>
    </section>
  );
}
